
// Pure, read-only topology QA scans over a project's own Feature list (plain
// GeoJSON-shaped objects from FeatureManager.features - see Feature.js) - no
// DOM/THREE dependency, Level 1 (no-browser) unit testable. Every function
// here only ever REPORTS findings; none of them mutate a feature or call
// FeatureManager (task requirement: "identificere og præsentere problemer
// frem for automatisk at ændre brugerens data") - see TopologyQaPanel.js for
// the read-only UI that presents these results, and lineSplit.js's
// connectEndpoints() for the one explicit, user-triggered fix action the
// panel offers (never automatic).
//
// Deliberately NOT implemented here (see the task's own final report for
// why): generic whole-feature duplicate-geometry detection and "overshoot"
// detection - both need domain-specific assumptions this codebase has no
// principled default for, and a wrong heuristic here would produce noisy
// false positives that erode trust in the whole panel.

import {dist3D} from "./vectorEditing.js";
import {findSegmentPairIntersections, DEFAULT_PLANAR_TOLERANCE_M} from "./lineIntersection2D.js";
import {isValidPolygonRing} from "./polygonValidity.js";

export const DEFAULT_GAP_REPORT_MAX_M = 0.5; // metres - upper band for "worth surfacing as a possible gap to close"; independent of DEFAULT_PLANAR_TOLERANCE_M, which is the much tighter "already effectively touching" threshold
const ZERO_LENGTH_TOLERANCE_M = 1e-6; // metres - a segment/ring-edge this short is a digitizing artifact, not a real design choice, at any project scale this codebase operates at

function toXYZ(coord){
	return {x: coord[0], y: coord[1], z: coord.length > 2 ? coord[2] : 0};
}

function isEditableLineString(feature){
	return feature.geometry.type === "LineString" && Array.isArray(feature.geometry.coordinates) && feature.geometry.coordinates.length >= 2;
}

function collectLineStrings(features){
	return features.filter(isEditableLineString).map((feature) => ({
		feature,
		points: feature.geometry.coordinates.map(toXYZ),
	}));
}

// Endpoints eligible for connectivity analysis: a LineString's own first/
// last vertex, and a standalone Point feature (a Point IS its own single
// "endpoint" for this purpose - e.g. a valve/hydrant marker meant to sit
// exactly on a pipe's endpoint).
function collectEndpoints(features){
	const out = [];
	for(const feature of features){
		if(isEditableLineString(feature)){
			const coords = feature.geometry.coordinates;
			out.push({feature, index: 0, point: toXYZ(coords[0])});
			out.push({feature, index: coords.length - 1, point: toXYZ(coords[coords.length - 1])});
		}else if(feature.geometry.type === "Point" && Array.isArray(feature.geometry.coordinates)){
			out.push({feature, index: 0, point: toXYZ(feature.geometry.coordinates)});
		}
	}
	return out;
}

// ── Delte punkter (shared nodes) ────────────────────────────────────────
//
// Groups endpoints (across DIFFERENT features only - a feature's own two
// endpoints happening to be close is not a "shared node") that lie within
// `tolerance` of each other in 3D. Simple greedy single-anchor clustering,
// not a full pairwise transitive closure/union-find - deliberately: real
// junction clusters in digitized data are tight (2-4 endpoints essentially
// coincident, well within tolerance of one another and of the group's own
// first member), and a heavier algorithm would be solving a problem this
// data doesn't actually have (task section 10: avoid unnecessary
// complexity). Used both to report "N forbindelsespunkter fundet" in the QA
// panel and by the move-vertex "you're about to break a connection" warning
// (see DigitizationModule.js).
export function findCoincidentEndpointGroups(features, {tolerance = DEFAULT_PLANAR_TOLERANCE_M} = {}){
	const endpoints = collectEndpoints(features);
	const used = new Array(endpoints.length).fill(false);
	const groups = [];

	for(let i = 0; i < endpoints.length; i++){
		if(used[i]) continue;
		const group = [endpoints[i]];
		used[i] = true;
		for(let j = i + 1; j < endpoints.length; j++){
			if(used[j] || endpoints[j].feature.id === endpoints[i].feature.id) continue;
			if(dist3D(endpoints[i].point, endpoints[j].point) <= tolerance){
				group.push(endpoints[j]);
				used[j] = true;
			}
		}
		if(new Set(group.map((e) => e.feature.id)).size >= 2) groups.push(group);
	}

	return groups.map((group) => ({
		position: group[0].point,
		members: group.map((e) => ({featureId: e.feature.id, featureName: e.feature.properties.name, endpointIndex: e.index})),
	}));
}

// ── Mindre gaps ───────────────────────────────────────────────────────────
//
// Endpoint pairs (across different features) that are close but NOT already
// coincident - candidates a user may want to close via Forbind endpoints
// (geometry/lineSplit.js's connectEndpoints(), same underlying operation
// this file never performs itself). `minGap` excludes pairs already handled
// by findCoincidentEndpointGroups() above; `maxGap` is a much larger "worth
// looking at" band, not a connectivity/physical-connection tolerance.
export function findNearbyEndpointGaps(features, {maxGap = DEFAULT_GAP_REPORT_MAX_M, minGap = DEFAULT_PLANAR_TOLERANCE_M} = {}){
	const endpoints = collectEndpoints(features);
	const results = [];

	for(let i = 0; i < endpoints.length; i++){
		for(let j = i + 1; j < endpoints.length; j++){
			if(endpoints[i].feature.id === endpoints[j].feature.id) continue;
			const gap = dist3D(endpoints[i].point, endpoints[j].point);
			if(gap > minGap && gap <= maxGap){
				results.push({
					a: {featureId: endpoints[i].feature.id, featureName: endpoints[i].feature.properties.name, endpointIndex: endpoints[i].index, point: endpoints[i].point},
					b: {featureId: endpoints[j].feature.id, featureName: endpoints[j].feature.properties.name, endpointIndex: endpoints[j].index, point: endpoints[j].point},
					gap,
				});
			}
		}
	}

	return results;
}

// ── Nul-længde segmenter ─────────────────────────────────────────────────
//
// Any consecutive vertex pair (LineString segments, or Polygon ring edges,
// outer ring only - see findInvalidPolygons()'s own comment on why holes
// aren't modelled) closer together than ZERO_LENGTH_TOLERANCE_M. This also
// catches exact/near-duplicate consecutive vertices - the same underlying
// symptom (task's "zero-length segments" and "dublerede eller næsten
// dublerede vertices" are one and the same check here, not two).
export function findZeroLengthSegments(features){
	const results = [];
	for(const feature of features){
		const type = feature.geometry.type;
		if(type !== "LineString" && type !== "Polygon") continue;

		const ring = type === "Polygon" ? (feature.geometry.coordinates && feature.geometry.coordinates[0]) : feature.geometry.coordinates;
		if(!Array.isArray(ring)) continue;

		for(let i = 0; i < ring.length - 1; i++){
			const length = dist3D(toXYZ(ring[i]), toXYZ(ring[i + 1]));
			if(length <= ZERO_LENGTH_TOLERANCE_M){
				results.push({featureId: feature.id, featureName: feature.properties.name, vertexIndex: i, length});
			}
		}
	}
	return results;
}

// ── Utilsigtede selv-skæringer (LineString) ──────────────────────────────
//
// Polygon rings already get this exact check via
// geometry/polygonValidity.js's isValidPolygonRing() (called by
// findInvalidPolygons() below and, live, as a mandatory post-condition on
// every Polygon edit - VectorEditingMenu.js). This extends the same
// underlying primitive (findSegmentPairIntersections, skipAdjacent: true) to
// ordinary open LineString features, which had no self-intersection check
// at all before this task.
export function findSelfIntersections(features, options = {}){
	const results = [];
	for(const {feature, points} of collectLineStrings(features)){
		const hits = findSegmentPairIntersections(points, points, {skipAdjacent: true, ...options});
		for(const hit of hits){
			results.push({featureId: feature.id, featureName: feature.properties.name, ...hit});
		}
	}
	return results;
}

// ── Ugyldige polygoner ────────────────────────────────────────────────────
//
// Pure reporting - no new geometry logic. Re-checks every existing Polygon
// feature's outer ring against the already-established
// isValidPolygonRing(), surfacing any that predate this check (digitized
// before it existed) or were otherwise left invalid. Only the outer ring is
// considered - this codebase has no hole/multi-ring polygon support anywhere
// (confirmed: CSVFeatureExporter.js only ever reads coordinates[0], and
// FeatureVisual has no second-ring concept), so there is nothing further to
// check.
export function findInvalidPolygons(features){
	const results = [];
	for(const feature of features){
		if(feature.geometry.type !== "Polygon") continue;
		const ring = feature.geometry.coordinates && feature.geometry.coordinates[0];
		if(!Array.isArray(ring) || ring.length === 0) continue;

		// FeatureVisual.toCoordinates() always closes the stored ring by
		// repeating the first vertex as the last - isValidPolygonRing()
		// expects the OPEN form (no repeated closing vertex, matching
		// FeatureVisual.points itself), so drop it here before checking
		const closesExactly = ring.length > 1 && dist3D(toXYZ(ring[0]), toXYZ(ring[ring.length - 1])) < 1e-9;
		const openRing = closesExactly ? ring.slice(0, -1) : ring;

		if(!isValidPolygonRing(openRing)){
			results.push({featureId: feature.id, featureName: feature.properties.name});
		}
	}
	return results;
}
